import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingCart, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();

  const fetchProduct = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${API_URL}/products/${id}`);
      setProduct(response.data);
    } catch (err) {
      setError('Unable to load product. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const handleAddToCart = async () => {
    await addToCart(product.id, quantity);
    toast.success(`${quantity} x ${product.name} added to cart!`);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h2 className="text-2xl font-semibold mb-4">{error || 'Product not found'}</h2>
        <Link to="/shop">
          <Button>Back to Shop</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/shop" className="inline-flex items-center text-medical-primary hover:underline mb-6" data-testid="back-to-shop">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Shop
        </Link>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
            <div className="aspect-square bg-gray-50 rounded-lg overflow-hidden">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-detail-image"
              />
            </div>

            <div className="flex flex-col">
              <span className="text-sm text-medical-primary font-medium mb-2" data-testid="product-category">{product.category}</span>
              <h1 className="text-3xl font-heading font-semibold mb-4" data-testid="product-detail-name">{product.name}</h1>
              <p className="text-gray-600 mb-6 leading-relaxed" data-testid="product-detail-description">{product.description}</p>
              
              <div className="mb-6">
                <span className="text-4xl font-bold text-medical-primary" data-testid="product-detail-price">${product.price.toFixed(2)}</span>
              </div>

              <div className="mb-6">
                {product.stock > 0 ? (
                  <span className="text-green-600 font-medium" data-testid="stock-status">In Stock ({product.stock} available)</span>
                ) : (
                  <span className="text-red-600 font-medium" data-testid="stock-status">Out of Stock</span>
                )}
              </div>

              {product.stock > 0 && (
                <div className="flex items-center gap-4 mb-6">
                  <label className="font-medium">Quantity:</label>
                  <div className="flex items-center border border-gray-300 rounded-md">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-4 py-2 hover:bg-gray-100"
                      data-testid="decrease-quantity"
                    >
                      -
                    </button>
                    <span className="px-6 py-2 border-x" data-testid="quantity-display">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      className="px-4 py-2 hover:bg-gray-100"
                      data-testid="increase-quantity"
                    >
                      +
                    </button>
                  </div>
                </div>
              )}

              <div className="flex gap-4 mt-auto">
                <Button
                  onClick={handleAddToCart}
                  disabled={product.stock === 0}
                  className="bg-medical-primary hover:bg-medical-primary/90 flex-1"
                  size="lg"
                  data-testid="add-to-cart-detail-button"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Add to Cart
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;